/**
 * Reveal flow — displays a wallet's private key after the user re-types
 * their wallet password. Runs ONLY inside the iframe served from
 * `auth.horuswallet.com`; the partner page renders this via
 * `<HorusRevealModal>` (or `useExportWallet`'s default UI shim).
 *
 * The partner page MUST NOT receive the private key. The postMessage
 * we send back at the end carries only `{ type: 'reveal_complete',
 * viewed: boolean }` — no key material crosses the iframe boundary.
 *
 * Wire shape:
 *   URL: ?flow=reveal&token=<revealToken>&origin=<partner>&state=<corr>
 *
 *   1. Read `revealToken` from URL.
 *   2. Render: password input + Reveal button.
 *   3. On submit, POST to /exportWallet/redeem { revealToken, password }.
 *   4. On success: render the private key (monospaced, copyable, hidden
 *      behind a "Tap to reveal" placeholder so a casual shoulder-surf
 *      doesn't catch it).
 *   5. On dismiss: postRevealComplete(ctx, viewed=true|false), then close.
 */

import { PostMessageContext } from '../postmessage';
import {
  el,
  mount,
  showError,
  clearError,
  setBusy,
  postCancel,
  postError,
  postRevealComplete,
  getQueryParam,
} from '../util';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://api.horuswallet.com';

export function renderRevealFlow(ctx: PostMessageContext): void {
  const revealToken = getQueryParam('token');
  if (!revealToken) {
    mount(
      el('div', {}, [
        el('h1', {}, ['Export wallet']),
        el('p', { class: 'error' }, ['Missing reveal token.']),
      ])
    );
    postError(ctx, 'reveal/missing-token', 'Reveal token is required.');
    return;
  }

  // ── Password prompt ──────────────────────────────────────────────
  let viewed = false;

  const titleEl = el('h1', {}, ['Export wallet']);
  const subtitleEl = el('p', { class: 'subtitle' }, [
    'Confirm your wallet password to reveal the private key. Save this key somewhere safe — anyone who has it can spend your wallet.',
  ]);
  const errorBox = el('p', { 'data-error': 'true', class: 'error', style: 'display:none' });

  const passwordInput = el('input', {
    type: 'password',
    autocomplete: 'current-password',
    placeholder: 'Wallet password',
    required: true,
  }) as HTMLInputElement;

  const submit = el('button', { type: 'submit' }, ['Reveal']) as HTMLButtonElement;
  const cancel = el('button', { type: 'button', class: 'ghost' }, ['Cancel']);

  const form = el('form', {}, [
    passwordInput,
    submit,
    cancel,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    clearError();
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/exportWallet/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          revealToken,
          password: passwordInput.value,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // 401 = wrong password (token already consumed, so user has to
        // restart from /grant — surface that clearly).
        if (res.status === 401) {
          showError(
            'Wrong password. For your safety, please request a new reveal token from the app and try again.'
          );
        } else if (res.status === 409) {
          showError('This reveal token has already been used. Request a new one.');
        } else {
          showError(data?.message ?? `Reveal failed (HTTP ${res.status}).`);
        }
        return;
      }
      const pk: string = data.privateKey;
      const addr: string | undefined = data.walletAddress;
      renderRevealedKey(pk, addr);
    } catch (err: any) {
      showError(err?.message ?? 'Network error while revealing key.');
    } finally {
      setBusy(false);
    }
  });

  cancel.addEventListener('click', () => {
    postCancel(ctx);
  });

  mount(
    el('div', {}, [titleEl, subtitleEl, errorBox, form])
  );

  // ── Revealed-key view ────────────────────────────────────────────
  function renderRevealedKey(privateKey: string, walletAddress?: string): void {
    viewed = true;

    const keyHidden = el('div', {
      class: 'reveal-blur',
      style:
        'font-family:monospace;padding:12px;background:#f4f4f4;border-radius:8px;word-break:break-all;cursor:pointer;filter:blur(6px);user-select:none;',
    }, [privateKey]) as HTMLDivElement;
    keyHidden.addEventListener('click', () => {
      keyHidden.style.filter = 'none';
      keyHidden.style.userSelect = 'text';
    });

    const copyBtn = el('button', { type: 'button' }, ['Copy to clipboard']);
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(privateKey);
        (copyBtn as HTMLButtonElement).textContent = 'Copied';
        setTimeout(() => {
          (copyBtn as HTMLButtonElement).textContent = 'Copy to clipboard';
        }, 1500);
      } catch {
        showError('Copy failed — select the key manually.');
      }
    });

    const doneBtn = el('button', { type: 'button' }, ['Done']);
    doneBtn.addEventListener('click', () => {
      // Wipe the displayed key from the DOM as best-effort before we
      // signal completion. (Browsers won't actually scrub the JS string
      // from memory, but reducing DOM lifetime narrows the window where
      // a screenshot or extension snapshot would catch it.)
      keyHidden.textContent = '';
      postRevealComplete(ctx, viewed);
    });

    const warning = el('p', { class: 'subtitle' }, [
      'Never paste this private key into any website. Treat it like the master key to a safe — anyone with it can move your funds without your password.',
    ]);

    mount(
      el('div', {}, [
        el('h1', {}, ['Your private key']),
        walletAddress
          ? el('p', { class: 'subtitle' }, [`Address: ${walletAddress}`])
          : el('span'),
        el('p', { class: 'subtitle' }, ['Tap to reveal:']),
        keyHidden,
        el('div', { style: 'display:flex;gap:8px;margin-top:16px;' }, [copyBtn, doneBtn]),
        warning,
      ])
    );
  }
}
